import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { ChevronDown } from 'lucide-react';
import styles from './TicketDetailsCard.module.css';
import { LoadingSpinner } from './LoadingSpinner';
import { Snackbar } from './Snackbar';
import { IgnoreModal } from './IgnoreModal';
import { TICKET_CONFIG, TicketAPI } from './ticketConfig';

const StatusBadge = ({ status, deferredDays }) => {
  return (
    <div className={styles.statusContainer}>
      <span className={styles.statusBadge}>
        {status}
      </span>
      {status === 'Deferred' && deferredDays && (
        <span className={styles.deferredInfo}>
          for {deferredDays} days
        </span>
      )}
    </div>
  );
};

StatusBadge.propTypes = {
  status: PropTypes.string.isRequired,
  deferredDays: PropTypes.number
};

const EffortBadge = ({ effort, effortType }) => {
  const getEffortStyle = (effortType) => {
    switch (effortType) {
      case 'high':
        return { background: '#dc2626', color: 'white' };
      case 'medium':
        return { background: '#ea580c', color: 'white' };
      case 'low':
        return { background: '#16a34a', color: 'white' };
      default:
        return { background: '#6b7280', color: 'white' };
    }
  };

  return (
    <div className={styles.prioritySection}>
      <span 
        className={styles.priorityBadge}
        style={getEffortStyle(effortType)}
      >
        {effort}
      </span>
    </div>
  );
};

EffortBadge.propTypes = {
  effort: PropTypes.string.isRequired,
  effortType: PropTypes.string.isRequired
};

export const TicketDetailsCard = ({ 
  id, 
  title, 
  description, 
  effort, 
  system, 
  statusType: initialStatusType,
  effortType,
  onAccept,
  onIgnore
}) => {
  // State management for ticket actions
  const [statusType, setStatusType] = useState(initialStatusType);
  const [deferredDays, setDeferredDays] = useState(null);
  const [isAcceptLoading, setIsAcceptLoading] = useState(false);
  const [showSnackbar, setShowSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [showIgnoreModal, setShowIgnoreModal] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  
  // Generate recommendations based on ticket data
  const recommendations = TICKET_CONFIG.generateRecommendations(effortType, system, id);

  // Helper function to render formatted recommendation content
  const renderRecommendationContent = (content) => {
    if (!content) return null;

    console.log('Original content:', content);

    // Normalize line breaks and split by double line breaks to create sections
    const normalizedContent = content.replace(/\r\n/g, '\n').replace(/\r/g, '\n');
    const sections = normalizedContent.split(/\n\s*\n/).filter(section => section.trim());
    
    console.log('Sections found:', sections.length, sections);
    
    return sections.map((section, sectionIndex) => {
      const trimmedSection = section.trim();
      
      console.log(`Processing section ${sectionIndex}:`, trimmedSection.substring(0, 50) + '...');
      
      // Handle horizontal rules (---)
      if (trimmedSection === '---') {
        return <hr key={`hr-${sectionIndex}-${Math.random()}`} className={styles.recommendationDivider} />;
      }
      
      // Handle main headers (# Header)
      if (trimmedSection.startsWith('# ')) {
        const headerText = trimmedSection.replace(/^# /, '');
        return (
          <h3 key={`h3-${sectionIndex}-${headerText.slice(0, 10)}`} className={styles.recommendationMainHeader}>
            {parseInlineFormatting(headerText)}
          </h3>
        );
      }
      
      // Handle sub headers (## Header)
      if (trimmedSection.startsWith('## ')) {
        const headerText = trimmedSection.replace(/^## /, '');
        return (
          <h4 key={`h4-${sectionIndex}-${headerText.slice(0, 10)}`} className={styles.recommendationSubHeader}>
            {parseInlineFormatting(headerText)}
          </h4>
        );
      }
      
      // Handle code blocks (```sql)
      if (trimmedSection.includes('```')) {
        const codeMatch = trimmedSection.match(/```(\w+)?\n?([\s\S]*?)```/);
        if (codeMatch) {
          const language = codeMatch[1] || '';
          const codeContent = codeMatch[2].trim();
          return (
            <div key={`code-${sectionIndex}-${Math.random()}`} className={styles.recommendationCodeBlock}>
              {language && <span className={styles.codeLanguage}>{language.toUpperCase()}</span>}
              <pre className={styles.codeContent}>
                <code>{codeContent}</code>
              </pre>
            </div>
          );
        }
      }
      
      // Handle bullet points (starting with -)
      if (trimmedSection.includes('\n- ') || trimmedSection.startsWith('- ')) {
        const lines = trimmedSection.split('\n').filter(line => line.trim());
        const bullets = [];
        let currentParagraph = [];
        
        lines.forEach((line, lineIndex) => {
          const trimmedLine = line.trim();
          if (trimmedLine.startsWith('- ')) {
            // If we have accumulated paragraph text, add it first
            if (currentParagraph.length > 0) {
              bullets.push(
                <p key={`para-${sectionIndex}-${lineIndex}-${Math.random()}`} className={styles.recommendationParagraph}>
                  {parseInlineFormatting(currentParagraph.join(' '))}
                </p>
              );
              currentParagraph = [];
            }
            // Add bullet point
            bullets.push(
              <div key={`bullet-${sectionIndex}-${lineIndex}-${Math.random()}`} className={styles.recommendationBullet}>
                <span className={styles.bulletIcon}>•</span>
                <span className={styles.bulletText}>{parseInlineFormatting(trimmedLine.substring(2))}</span>
              </div>
            );
          } else if (trimmedLine.startsWith('  - ')) {
            // Handle nested bullet points
            bullets.push(
              <div key={`nested-bullet-${sectionIndex}-${lineIndex}-${Math.random()}`} className={styles.recommendationNestedBullet}>
                <span className={styles.bulletIcon}>◦</span>
                <span className={styles.bulletText}>{parseInlineFormatting(trimmedLine.substring(4))}</span>
              </div>
            );
          } else if (trimmedLine) {
            currentParagraph.push(trimmedLine);
          }
        });
        
        // Add any remaining paragraph text
        if (currentParagraph.length > 0) {
          bullets.push(
            <p key={`para-${sectionIndex}-final-${Math.random()}`} className={styles.recommendationParagraph}>
              {parseInlineFormatting(currentParagraph.join(' '))}
            </p>
          );
        }
        
        return bullets;
      }
      
      // Handle regular paragraphs
      return (
        <p key={`para-${sectionIndex}-${Math.random()}`} className={styles.recommendationParagraph}>
          {parseInlineFormatting(trimmedSection)}
        </p>
      );
    });
  };

  // Helper function to parse inline formatting (**bold**, `code`)
  const parseInlineFormatting = (text) => {
    if (typeof text !== 'string') return text;
    
    const parts = [];
    let currentIndex = 0;
    
    // Handle **bold** text
    const boldRegex = /\*\*(.*?)\*\*/g;
    let boldMatch;
    
    while ((boldMatch = boldRegex.exec(text)) !== null) {
      // Add text before the match
      if (boldMatch.index > currentIndex) {
        const beforeText = text.slice(currentIndex, boldMatch.index);
        parts.push(...parseCodeFormatting(beforeText, parts.length));
      }
      
      // Add bold text
      parts.push(
        <strong key={`bold-${parts.length}`} className={styles.boldText}>
          {boldMatch[1]}
        </strong>
      );
      
      currentIndex = boldMatch.index + boldMatch[0].length;
    }
    
    // Add remaining text
    if (currentIndex < text.length) {
      const remainingText = text.slice(currentIndex);
      parts.push(...parseCodeFormatting(remainingText, parts.length));
    }
    
    return parts.length > 0 ? parts : text;
  };

  // Helper function to parse `code` formatting
  const parseCodeFormatting = (text, startIndex) => {
    const parts = [];
    let currentIndex = 0;
    
    const codeRegex = /`(.*?)`/g;
    let codeMatch;
    
    while ((codeMatch = codeRegex.exec(text)) !== null) {
      // Add text before the match
      if (codeMatch.index > currentIndex) {
        parts.push(text.slice(currentIndex, codeMatch.index));
      }
      
      // Add code text
      parts.push(
        <code key={`code-${startIndex}-${parts.length}`} className={styles.inlineCode}>
          {codeMatch[1]}
        </code>
      );
      
      currentIndex = codeMatch.index + codeMatch[0].length;
    }
    
    // Add remaining text
    if (currentIndex < text.length) {
      parts.push(text.slice(currentIndex));
    }
    
    return parts.length > 0 ? parts : [text];
  };

  const getBorderColor = (effortType) => {
    switch (effortType) {
      case 'high':
        return '#dc2626';
      case 'medium':
        return '#ea580c';
      case 'low':
        return '#16a34a';
      default:
        return '#404040';
    }
  };

  // Handle Accept button click
  const handleAccept = async () => {
    setIsAcceptLoading(true);
    
    try {
      // Use configurable API call (currently simulated)
      const response = await TicketAPI.acceptTicket(id);
      
      if (response.success) {
        // Update status to 'workInProgress' to hide buttons
        setStatusType('WORK IN PROGRESS');
        
        setSnackbarMessage(`CR Number ${response.crNumber} created.`);
        setShowSnackbar(true);
        
        // Call parent onAccept if provided
        if (onAccept) {
          onAccept(id, { 
            crNumber: response.crNumber,
            newStatus: 'workInProgress'
          });
        }
      }
    } catch (error) {
      console.error('Error accepting ticket:', error);
      // Show error snackbar
    } finally {
      setIsAcceptLoading(false);
    }
  };

  // Handle Ignore button click
  const handleIgnoreClick = () => {
    setShowIgnoreModal(true);
  };

  // Handle ignore with specific days
  const handleIgnore = async (days) => {
    try {
      // Use configurable API call (currently simulated)
      const response = await TicketAPI.ignoreTicket(id, days);
      
      if (response.success) {
        setStatusType(response.newStatus);
        setDeferredDays(response.deferredDays);
        
        // Call parent onIgnore if provided
        if (onIgnore) {
          onIgnore(id, { 
            days: response.deferredDays, 
            newStatus: response.newStatus 
          });
        }
      }
    } catch (error) {
      console.error('Error ignoring ticket:', error);
      // Show error snackbar
    }
  };

  // Close snackbar
  const handleCloseSnackbar = () => {
    setShowSnackbar(false);
  };

  // Close ignore modal
  const handleCloseIgnoreModal = () => {
    setShowIgnoreModal(false);
  };

  // Toggle recommendations section
  const toggleRecommendations = () => {
    setIsExpanded(!isExpanded);
  };

  return (
    <div 
      className={styles.card}
      style={{ borderColor: getBorderColor(effortType) }}
    >
      <div className={styles.header}>
        <div className={styles.idSection}>
          <span className={styles.ticketId}>{id}</span>
          <EffortBadge effort={effort} effortType={effortType} />
          <span className={styles.systemBadge}>{system}</span>
        </div>
        <StatusBadge status={statusType} deferredDays={deferredDays} />
      </div>

      <div className={styles.content}>
        <h3 className={styles.title}>{title}</h3>
        <div className={styles.descriptionRow}>
          <p className={styles.description}>{description}</p>
          {statusType.toLowerCase() === 'pending' && (
            <div className={styles.actions}>
              <button 
                className={styles.acceptButton}
                onClick={handleAccept}
                disabled={isAcceptLoading}
              >
                {isAcceptLoading ? (
                  <>
                    <LoadingSpinner size="small" />
                    <span style={{ marginLeft: '8px' }}>Processing...</span>
                  </>
                ) : (
                  'Accept'
                )}
              </button>
              <button 
                className={styles.ignoreButton}
                onClick={handleIgnoreClick}
                disabled={isAcceptLoading}
              >
                Ignore
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Chevron toggle for recommendations */}
      <div className={styles.expandToggle}>
        <button 
          className={styles.chevronButton}
          onClick={toggleRecommendations}
          aria-label={isExpanded ? 'Hide recommendations' : 'Show recommendations'}
          style={{ 
            '--hover-color': getBorderColor(effortType)
          }}
        >
          <ChevronDown 
            className={`${styles.chevronIcon} ${isExpanded ? styles.rotated : ''}`}
          />
        </button>
      </div>

      {/* Expandable recommendations section */}
      {isExpanded && (
        <div className={styles.recommendationsSection}>
          <div 
            className={styles.recommendationsHeader}
            style={{ borderTopColor: getBorderColor(effortType) }}
          >
            <h4 className={styles.recommendationsTitle}>Recommendations</h4>
          </div>
          <div className={styles.recommendationsList}>
            {recommendations.map((recommendation, index) => (
              <div key={`${id}-rec-${index}-${String(recommendation).slice(0, 20)}`} className={styles.recommendationItem}>
                {/* Check if this is a formatted recommendation (long text with special formatting) */}
                {String(recommendation).length > 500 ? (
                  <div className={styles.formattedRecommendation}>
                    {renderRecommendationContent(recommendation)}
                  </div>
                ) : (
                  <>
                    <span 
                      className={styles.bulletPoint}
                      style={{ color: getBorderColor(effortType) }}
                    >
                      •
                    </span>
                    <span className={styles.recommendationText}>{recommendation}</span>
                  </>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Snackbar for success message */}
      <Snackbar
        message={snackbarMessage}
        type="success"
        isVisible={showSnackbar}
        onClose={handleCloseSnackbar}
        duration={TICKET_CONFIG.SNACKBAR_DURATION}
      />

      {/* Modal for ignore options */}
      <IgnoreModal
        isOpen={showIgnoreModal}
        onClose={handleCloseIgnoreModal}
        onIgnore={handleIgnore}
      />
    </div>
  );
};

TicketDetailsCard.propTypes = {
  id: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  description: PropTypes.string.isRequired,
  effort: PropTypes.string.isRequired,
  system: PropTypes.string.isRequired,
  statusType: PropTypes.string.isRequired,
  effortType: PropTypes.string.isRequired,
  onAccept: PropTypes.func,
  onIgnore: PropTypes.func
};